import React, { useEffect, useRef, useState } from 'react';
import { crewOperation } from '../crewAccess';
import { useEquipmentControls } from '../equipmentRuntime';

export type PickupState = {
  shift_id: string; revision: number; confirmed: boolean; can_edit: boolean;
  items: { id: string; text: string; checked: boolean }[];
  outstanding: number; gear_completed: boolean; equipment_changed_since_pickup: boolean;
};

export function CrewPickupPanel({ shiftId, missionKey, onState }: {
  shiftId: string; missionKey: string; onState: (state: PickupState | null) => void;
}) {
  const controls=useEquipmentControls();
  const [state, setState] = useState<PickupState | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  useEffect(() => {
    const effectGeneration = ++generation.current;
    let active = true;
    setState(null); onState(null); setBusy(true);
    crewOperation('crew_pickup', { p_shift: shiftId }).then(data => {
      if (active) { setState(data); onState(data); }
    }).catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; generation.current = effectGeneration + 1; };
  }, [shiftId, missionKey, onState, reload]);

  const tick = async (id: string, checked: boolean) => {
    if (!state || busy) return;
    const requestGeneration = generation.current;
    setBusy(true); setError('');
    try {
      const saved = await crewOperation('crew_pickup_tick', {
        p_shift: shiftId, p_revision: state.revision, p_item: id,
        p_checked: checked, p_request_id: crypto.randomUUID(),
      });
      if (generation.current === requestGeneration) { setState(saved); onState(saved); }
    } catch (err: any) {
      if (generation.current !== requestGeneration) return;
      setError(err.message || 'Pickup could not be saved. Please reload.');
      setState(null); onState(null);
    } finally { if (generation.current === requestGeneration) setBusy(false); }
  };

  return <section aria-label="Shift equipment pickup" className="w-full min-w-0 rounded-2xl border border-cyan-500/30 bg-slate-900 p-4 space-y-3">
    <h3 className="text-base font-semibold text-white">Equipment pickup</h3>
    {!controls.pickup && <p role="status">Pickup temporarily read-only. Existing progress is preserved.</p>}
    {error && <p role="alert" className="text-sm text-amber-300 break-words">{error}</p>}
    {!state && <p role="status" className="text-sm text-slate-300">{busy ? 'Loading confirmed equipment…' : 'Pickup unavailable. Reload to try again.'}</p>}
    {state && !state.confirmed && <p className="text-sm text-slate-300">Awaiting Admin equipment confirmation for this Shift. The old quote packing list is not used for pickup.</p>}
    {state?.confirmed && <>
      {state.equipment_changed_since_pickup && <p role="status" className="rounded-xl bg-amber-500/10 p-3 text-sm text-amber-200">Equipment changed since pickup. Current items remain outstanding below; your historical Gear Collected completion is preserved.</p>}
      {state.items.length === 0 ? <p className="text-sm text-slate-300">Confirmed: no equipment required. Gear Collected is not required.</p> : <>
        <p className="text-sm text-slate-300">{state.items.length - state.outstanding} of {state.items.length} collected · Revision {state.revision}</p>
        <div className="space-y-2">
          {state.items.map(item => <button key={item.id} type="button" role="checkbox" aria-checked={item.checked}
            disabled={busy || !state.can_edit || !controls.pickup} onClick={() => tick(item.id, !item.checked)}
            className={`flex w-full min-w-0 min-h-[44px] items-start gap-3 rounded-xl border p-3 text-left text-sm font-medium disabled:opacity-60 ${item.checked ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-100' : 'border-slate-600 text-slate-100'}`}>
            <span aria-hidden="true" className="shrink-0">{item.checked ? '☑' : '☐'}</span>
            <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{item.text}</span>
          </button>)}
        </div>
      </>}
    </>}
    <button type="button" disabled={busy} onClick={() => { setError(''); setReload(n => n + 1); }} className="min-h-[44px] px-3 text-sm font-semibold text-cyan-300 disabled:opacity-50">Reload equipment</button>
  </section>;
}
